import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_workflows: number
  active_workflows: number
  executions_today: number
  executions_mtd: number
  success_rate: number
  avg_duration_sec: number
}

interface Workflow {
  id: string
  name: string
  steps: number
  executions_day: number
  success_rate: number
  status: string
}

interface Execution {
  id: string
  workflow: string
  started: string
  duration: string
  status: string
}

export default function IntegrationOrchestrationPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [executions, setExecutions] = useState<Execution[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, workflowsRes, executionsRes] = await Promise.all([
          fetch('/api/integrationorchestration/statistics'),
          fetch('/api/integrationorchestration/workflows'),
          fetch('/api/integrationorchestration/executions')
        ])
        setStatistics(await statsRes.json())
        setWorkflows(await workflowsRes.json())
        setExecutions(await executionsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Running': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'Failed': 'bg-red-100 text-red-800',
      'Paused': 'bg-yellow-100 text-yellow-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Integration Orchestration...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Integration Orchestration</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_workflows}</div>
              <div className="text-sm text-gray-500">Total Workflows</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_workflows}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.executions_today.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Executions Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.executions_mtd / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-500">Executions MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.success_rate}%</div>
              <div className="text-sm text-gray-500">Success Rate</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_duration_sec}s</div>
              <div className="text-sm text-gray-500">Avg Duration</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Workflows</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Workflow</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Steps</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Exec/Day</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Success</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {workflows.map((wf) => (
                    <tr key={wf.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{wf.name}</td>
                      <td className="px-4 py-3 text-gray-600">{wf.steps}</td>
                      <td className="px-4 py-3 text-gray-600">{wf.executions_day.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600">{wf.success_rate}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(wf.status)}`}>
                          {wf.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Recent Executions</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Workflow</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Started</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {executions.map((exec) => (
                    <tr key={exec.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{exec.workflow}</td>
                      <td className="px-4 py-3 text-gray-600">{exec.started}</td>
                      <td className="px-4 py-3 text-gray-600">{exec.duration}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(exec.status)}`}>
                          {exec.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
